import Bugsnag from '@bugsnag/js';
import {
  addOrganisationBank as _addOrganisationBank,
  addOrganisationReport as _addOrgReport,
  addOrganisationResearch as _addOrganisationResearch,
  addOrganisationUser as _addOrganisationUser,
  addResearch as _addResearch,
  addUpload as _addUpload,
  removeOrganisationBank as _removeOrganisationBank,
  removeOrganisationReport as _removeOrgReport,
  removeOrganisationResearch as _removeOrganisationResearch,
  removeOrganisationUser as _removeOrganisationUser,
  removeResearch as _removeResearch,
} from 'api';
import {
  UpdateCurrentAppUserMutationVariables,
  useUpdateCurrentAppUserMutation,
} from 'graphql/mutations/UpdateCurrentAppUser.generated';
import { useGetGlobalAdminDataQuery } from 'graphql/queries/GetGlobalAdminData.generated';
import { useGetGlobalDataQuery } from 'graphql/queries/GetGlobalData.generated';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useWrappedUrqlMutation } from 'toolbox';
import { useAuthWrapperContext } from 'toolbox/src/components/AuthWrapper/AuthWrapper';

const Context = createContext(undefined);

export const ApiProvider = ({ children }) => {
  const provider: any = useHasuraProvider();
  return <Context.Provider value={provider}>{children}</Context.Provider>;
};

export const useHasura: () => ReturnType<typeof useHasuraProvider> = () =>
  useContext(Context);

const useHasuraProvider = () => {
  const { user, username, groups, status } = useAuthWrapperContext();
  const isAdmin = groups?.includes('admin') || false;

  const [state, setState] = useState({
    loading: false,
    error: null,
  });

  const [globalData, _refreshGlobalData] = useGetGlobalDataQuery({
    variables: {
      userId: username,
    },
    pause: status !== 'loggedIn',
  });

  const [adminData, _refreshAdminData] = useGetGlobalAdminDataQuery({
    pause: status !== 'loggedIn' || !isAdmin,
  });

  const refreshGlobalData = () => {
    _refreshGlobalData({
      requestPolicy: 'network-only',
    });
  };

  const refreshAdminData = () => {
    _refreshAdminData({
      requestPolicy: 'network-only',
    });
  };

  const app_user = globalData.data?.app_users_by_pk;
  const currencies = globalData.data?.currencies;
  const organisations = globalData.data?.organisations;
  const research = globalData.data?.research;

  const organisationBanks = adminData.data?.organisation_banks;
  const app_users = adminData.data?.app_users;
  const organisationUsers = adminData.data?.organisation_users;

  const app_userMutation = useWrappedUrqlMutation(
    useUpdateCurrentAppUserMutation,
  );

  useEffect(() => {
    // this runs once when the provider is first loaded
    if (status === 'loggedIn') {
      app_userMutation.dispatch({
        userId: username,
        modification: {
          last_logged_in: new Date().toISOString(),
        },
      });
    }
  }, [user]);

  function handleError(error) {
    if (error === 'not authenticated') {
      setState({ ...state, loading: false });
    } else {
      console.warn('warning:', error);
      Bugsnag.notify(error);
      setState({ ...state, loading: false, error: error });
    }
  }

  async function updateAppUser(
    modification: UpdateCurrentAppUserMutationVariables['modification'],
  ) {
    setState({ error: null, loading: true });
    try {
      await app_userMutation.dispatch({
        userId: username,
        modification,
      });
      // console.log("app_user:", updatedapp_user);
    } catch (error) {
      handleError(error);
    } finally {
      setState({ ...state, loading: false });
    }
  }

  async function addFileUpload(variables) {
    setState({ error: null, loading: true });
    try {
      const uploadResult = await _addUpload(variables);
      console.log('Upload:', uploadResult);
    } catch (error) {
      handleError(error);
    } finally {
      setState({ ...state, loading: false });
    }
  }

  async function addOrganisationBank(variables) {
    setState({ error: null, loading: true });
    try {
      const result = await _addOrganisationBank(variables);
      console.log('Updated Permissions:', result);
      refreshAdminData();
    } catch (error) {
      handleError(error);
    } finally {
      setState({ ...state, loading: false });
    }
  }

  async function removeOrganisationBank(variables) {
    setState({ error: null, loading: true });
    try {
      const result = await _removeOrganisationBank(variables);
      console.log('Updated Permissions:', result);
      refreshAdminData();
    } catch (error) {
      handleError(error);
    } finally {
      setState({ ...state, loading: false });
    }
  }

  async function addOrgReport(variables) {
    setState({ error: null, loading: true });
    try {
      await _addOrgReport(variables);
      refreshGlobalData();
    } catch (error) {
      handleError(error);
    } finally {
      setState({ ...state, loading: false });
    }
  }

  async function removeOrgReport(upload_id) {
    setState({ error: null, loading: true });
    try {
      await _removeOrgReport({ upload_id });
      refreshGlobalData();
    } catch (error) {
      handleError(error);
    } finally {
      setState({ ...state, loading: false });
    }
  }

  async function addOrganisationUser(variables) {
    setState({ error: null, loading: true });
    try {
      const result = await _addOrganisationUser(variables);
      console.log('Updated Permissions:', result);
      refreshGlobalData();
      refreshAdminData();
    } catch (error) {
      handleError(error);
    } finally {
      setState({ ...state, loading: false });
    }
  }

  async function removeOrganisationUser(variables) {
    setState({ error: null, loading: true });
    try {
      const result = await _removeOrganisationUser(variables);
      console.log('Updated Permissions:', result);
      refreshGlobalData();
      refreshAdminData();
    } catch (error) {
      handleError(error);
    } finally {
      setState({ ...state, loading: false });
    }
  }

  async function addResearch(variables) {
    setState({ error: null, loading: true });
    try {
      const result = await _addResearch(variables);
      refreshGlobalData();
      return result?.insert_Research?.returning[0] || null;
    } catch (error) {
      handleError(error);
    } finally {
      setState({ ...state, loading: false });
    }
  }

  async function removeResearch(variables) {
    setState({ error: null, loading: true });
    try {
      await _removeResearch(variables);
      refreshGlobalData();
    } catch (error) {
      handleError(error);
    } finally {
      setState({ ...state, loading: false });
    }
  }

  async function addOrganisationResearch(variables) {
    setState({ error: null, loading: true });
    try {
      await _addOrganisationResearch(variables);
      refreshAdminData();
    } catch (error) {
      handleError(error);
    } finally {
      setState({ ...state, loading: false });
    }
  }

  async function removeOrganisationResearch(variables) {
    setState({ error: null, loading: true });
    try {
      await _removeOrganisationResearch(variables);
      refreshAdminData();
    } catch (error) {
      handleError(error);
    } finally {
      setState({ ...state, loading: false });
    }
  }

  const derivedState = {
    ...state,
    loading: globalData.fetching || state.loading || adminData.fetching,
    error: state.error || globalData.error || adminData.error,
  };

  return {
    state: derivedState,
    app_user,
    currencies,
    organisations,
    research,
    refreshapp_user: refreshGlobalData,
    updateAppUser,
    addFileUpload,
    addResearch,
    removeResearch,
    addOrganisationResearch,
    addOrganisationBank,
    removeOrganisationBank,
    removeOrganisationResearch,
    organisationBanks,
    addOrgReport,
    removeOrgReport,
    app_users,
    organisationUsers,
    addOrganisationUser,
    removeOrganisationUser,
  };
};
